import { useState, useEffect } from "react";
import { toast } from "sonner";
import { GraduationCap, Briefcase, Coffee, Flag, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { getEmployeeSchedule, setDayException } from "../../services/api";

const TYPES = [
  { key: "school", label: "École", icon: GraduationCap, cls: "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" },
  { key: "work", label: "Travail", icon: Briefcase, cls: "border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100" },
  { key: "rest", label: "Repos", icon: Coffee, cls: "border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100" },
  { key: "holiday", label: "Férié", icon: Flag, cls: "border-red-300 bg-red-50 text-red-700 hover:bg-red-100" },
];

export const DayExceptionDialog = ({ open, onClose, employee, date, onChanged }) => {
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setCurrent(null);
      getEmployeeSchedule(employee.id)
        .then((r) => setCurrent((r.data.manual_overrides || {})[date] || null))
        .catch(() => {});
    }
  }, [open, employee, date]);

  if (!employee || !date) return null;

  const apply = async (type) => {
    try {
      setBusy(true);
      await setDayException(employee.id, { date, type });
      toast.success(type
        ? `Exception « ${TYPES.find((t) => t.key === type)?.label} » posée pour ${employee.first_name}`
        : "Exception retirée");
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="responsive-dialog max-w-sm overflow-y-auto" data-testid="day-exception-dialog">
        <DialogHeader>
          <DialogTitle className="text-base pr-6 break-words" data-testid="day-exception-title">
            Exception — {employee.first_name} {employee.last_name?.toUpperCase()}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 capitalize -mt-2" data-testid="day-exception-date">
          {new Date(date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          {current && <span className="ml-2 text-xs font-semibold text-[#26788A]">(actuel : {TYPES.find((t) => t.key === current)?.label})</span>}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {TYPES.map(({ key, label, icon: Icon, cls }) => (
            <button key={key} disabled={busy}
              className={`flex items-center gap-2 border rounded-xl px-3 py-3 text-sm font-semibold transition-colors ${cls} ${current === key ? "ring-2 ring-[#26788A]" : ""}`}
              onClick={() => apply(key)} data-testid={`exc-type-${key}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
        {current && (
          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 w-full whitespace-normal h-auto min-h-11" disabled={busy} onClick={() => apply(null)} data-testid="exc-remove-btn">
            {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Trash2 size={13} className="mr-1.5" />}
            Retirer l'exception (revenir au planning normal)
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
